/**
 * textSanitizer.js
 *
 * Post-processing utility that strips markdown artifacts and normalises
 * AI-generated text before it lands in resume fields.
 *
 * Applied in two places:
 *   1. MobileGeminiService.extractRelevantContent — after extraction
 *   2. MobileGeminiService.generateText — after generation
 */

/**
 * Strip all common markdown formatting from a string so it reads as
 * clean plain text suitable for a professional resume.
 *
 * Order of operations matters — bold/italic must be stripped before
 * we look for bullet characters so that `**- item**` is handled.
 */
export function stripMarkdown(text) {
  if (!text || typeof text !== 'string') return '';

  let cleaned = text;

  // 1. Remove markdown bold  **text**  or  __text__
  cleaned = cleaned.replace(/\*\*(.+?)\*\*/g, '$1');
  cleaned = cleaned.replace(/__(.+?)__/g, '$1');

  // 2. Remove markdown italic  *text*  or  _text_
  //    (single asterisk/underscore not at word boundary is left alone)
  cleaned = cleaned.replace(/(?<!\w)\*([^*\n]+?)\*(?!\w)/g, '$1');
  cleaned = cleaned.replace(/(?<!\w)_([^_\n]+?)_(?!\w)/g, '$1');

  // 3. Remove markdown headers  ## Header
  cleaned = cleaned.replace(/^#{1,6}\s+/gm, '');

  // 4. Remove markdown horizontal rules  --- or ***
  cleaned = cleaned.replace(/^[-*_]{3,}\s*$/gm, '');

  return cleaned.trim();
}

/**
 * Normalise bullet / list characters at the start of lines.
 *
 * Converts  •, -, *, >, ▪, ▸, ●  and numbered list prefixes (1., 2.)
 * into a consistent newline-separated format with no leading marker,
 * OR keeps a single consistent marker depending on the `style` option.
 *
 * @param {string} text
 * @param {'none' | 'dash' | 'dot'} style — 'none' removes markers entirely
 */
export function normaliseBullets(text, style = 'none') {
  if (!text || typeof text !== 'string') return '';

  const PREFIX = style === 'dash' ? '- ' : style === 'dot' ? '• ' : '';

  // Split into lines, strip each line's leading bullet/number
  const lines = text.split('\n').map(line => {
    let trimmed = line.trim();
    // Remove leading bullet characters:  • - * > ▪ ▸ ●
    trimmed = trimmed.replace(/^[•\-*>▪▸●]\s*/, '');
    // Remove leading numbered list:  1. 2) 3:
    trimmed = trimmed.replace(/^\d+[.):\-]\s*/, '');
    return trimmed;
  });

  // Remove empty lines and re-join with the chosen prefix
  return lines
    .filter(l => l.length > 0)
    .map(l => `${PREFIX}${l}`)
    .join('\n');
}

/**
 * Collapse excessive whitespace: multiple blank lines → one,
 * trailing/leading spaces on each line, etc.
 */
export function collapseWhitespace(text) {
  if (!text || typeof text !== 'string') return '';

  return text
    .replace(/[ \t]+/g, ' ')       // multiple spaces/tabs → single space
    .replace(/ *\n */g, '\n')       // trim spaces around newlines
    .replace(/\n{3,}/g, '\n\n')     // 3+ blank lines → one blank line
    .trim();
}

/**
 * Smart truncation: cut at a word/sentence boundary instead of mid-word.
 * Prefers cutting at the last period before the limit; falls back to
 * the last space.
 *
 * @param {string} text
 * @param {number} maxLength — maximum character count
 */
export function smartTruncate(text, maxLength) {
  if (!text || typeof text !== 'string') return '';
  if (text.length <= maxLength) return text;

  const slice = text.slice(0, maxLength);

  // Try to cut at the last sentence boundary (period followed by space or end)
  const lastPeriod = slice.lastIndexOf('. ');
  if (lastPeriod > maxLength * 0.6) {
    return slice.slice(0, lastPeriod + 1).trim();
  }

  // Fall back to last newline
  const lastNewline = slice.lastIndexOf('\n');
  if (lastNewline > maxLength * 0.6) {
    return slice.slice(0, lastNewline).trim();
  }

  // Fall back to last space
  const lastSpace = slice.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.5) {
    return slice.slice(0, lastSpace).trim();
  }

  // Absolute fallback: hard cut
  return slice.trim();
}

/**
 * Full sanitisation pipeline for resume field content.
 *
 * @param {string} text — raw AI output
 * @param {object} [opts]
 * @param {number} [opts.maxLength] — character budget (applies smart truncation)
 * @param {'none'|'dash'|'dot'} [opts.bulletStyle] — how to render list items
 */
export function sanitizeForResume(text, opts = {}) {
  const { maxLength, bulletStyle = 'none' } = opts;

  let result = stripMarkdown(text);
  result = normaliseBullets(result, bulletStyle);
  result = collapseWhitespace(result);

  if (maxLength && maxLength > 0) {
    result = smartTruncate(result, maxLength);
  }

  return result;
}

export default sanitizeForResume;
